// =============================================================================
// screens/ServerDetailScreen.tsx — COMMUNITY INTERIOR (reference IMG_5877)
//
// The channels-model community interior from the old app the client loves,
// recolored to the current lavender/purple system:
//   · Header: back · community icon + name + chevron (tap → Key Info) · search
//     (in-screen channel filter) · bell (→ Preferences) · "…" overflow.
//     Rendered in-screen (native header hidden) — kills the "‹ Tabs" back label.
//   · Tabs: Channels | Key Info | Preferences (purple underline segmented row).
//   · CHANNELS: pinned/featured square glass tiles (unread badges) + grouped
//     2-column tile grids ("All Channels" + any extra sections, "New!" pills).
//   · KEY INFO: about, stats (members/channels/created), member list.
//   · PREFERENCES: per-community notification level (all/mentions/nothing) +
//     mute — backend endpoint first, AsyncStorage fallback (works today).
//
// OVERFLOW MENU — only real actions render (NO dead items):
//   · Share    → React Native Share with the cryb://servers/:id deep link
//                (declared in navigation/linking.ts). iOS's share sheet includes
//                Copy, which covers "Copy link" — no clipboard dep exists.
//   · Leave    → POST /servers/:id/leave (existing endpoint), members only.
//   · Report / gear-settings are intentionally ABSENT: the backend has no
//     server-report endpoint and no server-settings surface exists in the app.
//
// DATA CONTRACTS + FALLBACKS:
//   GET /servers/:id                        → bare { server:{ …, Channel:[…], ServerMember:[…] } }
//                                             · non-member → 404 (Join preview, never a crash)
//   GET /servers/:id/channels?grouped=true  → sections[{ name, channels:[{id,name,type,unread,isNew}] }]
//     (backend agent is adding this). GRACEFUL FALLBACK: if it 404s or returns
//     the flat { channels } body, sections are derived CLIENT-SIDE from the flat
//     list (CATEGORY channels → section headers via parentId/categoryId; the
//     rest → "All Channels") so the screen works against prod TODAY.
//   POST /servers/:id/join · POST /servers/:id/leave → { success }
//   GET/PUT /servers/:id/notification-preferences (pending) → AsyncStorage
//     fallback via components/community/notificationPrefs.
//
// The LR5 live member-count realtime handling and the status-aware fetch
// (404 vs offline branching) are preserved from the previous version.
// =============================================================================
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity, TextInput,
  ActivityIndicator, RefreshControl, Alert, Share, Switch, useWindowDimensions,
} from 'react-native';
import CachedImage from '../components/ui/CachedImage';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import LinearGradient from 'react-native-linear-gradient';
import { Ionicons } from '../components/ui/Icon';
import type { RootStackParamList } from '../navigation/types';
import {
  colors, spacing, radius, fonts, getLayout, layout as layoutTokens,
  typographyScale,
} from '../constants/theme';
import { GlassCard, GlassSurface, GlassButton, glassTokens } from '../components/glass';
import ParticleBackground from '../components/ui/ParticleBackground';
import Asset3D from '../components/ui/Asset3D';
import {
  CommunityHeader, SegmentedTabs, OverflowMenu, PinnedChannelTile, ChannelGridTile,
  loadCommunityPrefs, saveCommunityPrefs, DEFAULT_PREFS,
  isCategory, isVoice, isRecent, toTile, deriveSections, parseGroupedSections, pickPinned,
} from '../components/community';
import type { ChannelTileData, CommunityPrefs, NotifLevel, OverflowAction, Channel, ChannelSection } from '../components/community';
import { api, getToken } from '../services/api';
import { joinServerRoom, leaveServerRoom, onUserEvent } from '../services/socket';

type Nav = NativeStackNavigationProp<RootStackParamList, 'ServerDetail'>;
type ServerRoute = RouteProp<RootStackParamList, 'ServerDetail'>;

const API_BASE = 'https://api.cryb.ai/api/v1';

const SERVER_GRADIENTS: [string, string][] = [
  ['#6C5CE7', '#a855f7'], ['#2563EB', '#3B82F6'], ['#16A34A', '#22C55E'],
  ['#F7931A', '#FFB347'], ['#DC2626', '#F87171'], ['#0D9488', '#2DD4BF'],
  ['#7C3AED', '#6366F1'], ['#DB2777', '#F472B6'],
];
function gradientFor(key: string): [string, string] {
  let h = 0;
  const s = String(key || '');
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return SERVER_GRADIENTS[h % SERVER_GRADIENTS.length];
}

// Plural-aware count label: pluralize(1,'member') → "1 member", (2) → "2 members".
function pluralize(n: number, singular: string, plural?: string): string {
  const safe = Number.isFinite(n) ? n : 0;
  const word = safe === 1 ? singular : (plural ?? `${singular}s`);
  return `${safe.toLocaleString()} ${word}`;
}

interface Member {
  id: string;
  username: string;
  displayName: string;
  avatar?: string;
  isOwner?: boolean;
}
interface ServerData {
  id: string;
  name: string;
  icon?: string | null;
  description?: string | null;
  createdAt?: string | null;
  ownerId?: string | null;
  channels: Channel[];
  members: Member[];
  memberCount: number;
}

// A lightweight "you can join this" preview when /servers/:id 404s for non-members.
interface ServerPreview {
  id: string;
  name: string;
  icon?: string | null;
  description?: string | null;
  memberCount: number;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'member'; server: ServerData }
  | { kind: 'join'; preview: ServerPreview }   // non-member 404 → join CTA
  | { kind: 'offline' }                          // network / unknown error
  | { kind: 'notfound' };                        // genuinely missing

type TabKey = 'channels' | 'info' | 'prefs';

// Status-aware fetch (api() hides the HTTP status; we need 404 vs other to branch).
// Returns { status, body } or { status:0 } on a network failure. Never throws.
async function fetchStatus(path: string): Promise<{ status: number; body: any }> {
  try {
    const token = await getToken();
    const res = await fetch(`${API_BASE}${path}`, {
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    let body: any = null;
    const raw = await res.text();
    if (raw) { try { body = JSON.parse(raw); } catch { body = null; } }
    return { status: res.status, body };
  } catch {
    return { status: 0, body: null };
  }
}

// Map a member row's nested User include to a flat identity, with fallbacks so a
// malformed/relation-drifted row never crashes the list (Prisma-drift bug class).
function normalizeMember(raw: any, ownerId?: string): Member | null {
  const u = raw?.User ?? raw?.user ?? raw ?? {};
  const uid = u.id ?? raw?.userId ?? '';
  if (!uid && !u.username) return null;
  return {
    id: String(uid || u.username),
    username: u.username ?? '',
    displayName: u.displayName ?? u.username ?? 'Member',
    avatar: u.avatar ?? undefined,
    isOwner: !!ownerId && uid === ownerId,
  };
}

// Channel-section helpers (isCategory/isVoice/isRecent/toTile/deriveSections/
// parseGroupedSections/pickPinned) are shared with CommunityScreen — see
// components/community/channelSections.ts.

export default function ServerDetailScreen() {
  const navigation = useNavigation<Nav>();
  const route = useRoute<ServerRoute>();
  const { id, initialTab } = route.params ?? ({} as { id: string; initialTab?: TabKey });
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const containerStyle = layout !== 'phone'
    ? { maxWidth: layout === 'desktop' ? 720 : 640, alignSelf: 'center' as const, width: '100%' as const }
    : undefined;

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState<TabKey>(initialTab ?? 'channels');
  const [joining, setJoining] = useState(false);
  // Channel sections (grouped endpoint or client-side fallback).
  const [sections, setSections] = useState<ChannelSection[]>([]);
  // In-screen channel search (header search icon).
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Overflow menu.
  const [menuOpen, setMenuOpen] = useState(false);
  // Per-community notification preferences (backend-or-local).
  const [prefs, setPrefs] = useState<CommunityPrefs>(DEFAULT_PREFS);
  const [prefsReady, setPrefsReady] = useState(false);
  // Current user id — resolved once so we can tell if you own this community
  // (owners cannot leave; surface that up front instead of via a rejected confirm).
  const [myId, setMyId] = useState<string | null>(null);

  // The native header is fully replaced by the in-screen CommunityHeader (which
  // owns the safe area) — this also removes the "‹ Tabs" route-name back label.
  useLayoutEffect(() => {
    navigation.setOptions({ headerShown: false });
  }, [navigation]);

  // navigate() to an already-mounted ServerDetail updates params without a
  // remount — honor a fresh initialTab (e.g. channel sheet → Key Info).
  useEffect(() => {
    if (initialTab) setTab(initialTab);
  }, [initialTab]);

  // Try to load a public preview (name/desc/memberCount) so a non-member 404 can
  // still show a meaningful Join card instead of a bare "join this server".
  const loadPreview = useCallback(async (sid: string): Promise<ServerPreview | null> => {
    try {
      const res = await api<any>(`/servers/discover?limit=100`);
      const servers = res.success
        ? (Array.isArray(res.data?.servers) ? res.data.servers : (Array.isArray(res.data) ? res.data : []))
        : [];
      const hit = servers.find((s: any) => String(s?.id) === String(sid));
      if (hit) {
        return {
          id: String(hit.id),
          name: hit.name ?? 'Community',
          icon: hit.icon ?? null,
          description: hit.description ?? null,
          memberCount: hit.memberCount ?? hit._count?.ServerMember ?? 0,
        };
      }
    } catch { /* fall through */ }
    return null;
  }, []);

  // Fetch grouped sections; fall back to client-side derivation from the flat
  // channel list already on the server payload. Never throws.
  const loadSections = useCallback(async (sid: string, flatChannels: Channel[]) => {
    const res = await api<any>(`/servers/${encodeURIComponent(sid)}/channels?grouped=true`);
    if (res.success) {
      const grouped = parseGroupedSections(res.data);
      if (grouped) { setSections(grouped); return; }
      // Endpoint answered with the flat { channels } body — derive client-side
      // from it (fresher than the server payload's include).
      const flat = Array.isArray(res.data?.channels) ? res.data.channels : null;
      if (flat) { setSections(deriveSections(flat)); return; }
    }
    setSections(deriveSections(flatChannels));
  }, []);

  const load = useCallback(async () => {
    const sid = String(id || '');
    if (!sid) { setState({ kind: 'notfound' }); return; }
    const { status, body } = await fetchStatus(`/servers/${encodeURIComponent(sid)}`);

    if (status === 0) { setState({ kind: 'offline' }); return; }

    if (status === 200 && body) {
      // Bare envelope: { server: {...} }. Be tolerant of a data-wrapped variant too.
      const s = body.server ?? body.data?.server ?? body.data ?? body;
      if (s && (s.id || s.name)) {
        const rawChannels = Array.isArray(s.Channel) ? s.Channel
          : (Array.isArray(s.channels) ? s.channels : []);
        const rawMembers = Array.isArray(s.ServerMember) ? s.ServerMember
          : (Array.isArray(s.members) ? s.members : []);
        const members = rawMembers
          .map((m: any) => normalizeMember(m, s.ownerId ?? s.ownerUserId))
          .filter(Boolean) as Member[];
        const channels: Channel[] = rawChannels.map((c: any) => ({
          id: c.id, name: c.name ?? 'channel', type: c.type,
          parentId: c.parentId ?? null, categoryId: c.categoryId ?? null,
          position: c.position ?? 0, topic: c.topic ?? c.description ?? null,
          createdAt: c.createdAt,
        }));
        setState({
          kind: 'member',
          server: {
            id: String(s.id ?? sid),
            name: s.name ?? 'Community',
            icon: s.icon ?? null,
            description: s.description ?? null,
            createdAt: s.createdAt ?? null,
            ownerId: s.ownerId ?? s.ownerUserId ?? null,
            memberCount: members.length || s.memberCount || s._count?.ServerMember || 0,
            channels,
            members,
          },
        });
        // Sections load independently (grouped endpoint → fallback derive).
        loadSections(sid, channels);
        return;
      }
    }

    if (status === 404 || status === 403) {
      // Non-member (404) or members-only channel list (403) → offer to join.
      const preview = await loadPreview(sid);
      setState(preview ? { kind: 'join', preview } : { kind: 'join', preview: { id: sid, name: 'Community', memberCount: 0 } });
      return;
    }

    // Any other status (5xx, etc.) → offline/error state with retry.
    setState({ kind: 'offline' });
  }, [id, loadPreview, loadSections]);

  useEffect(() => { load(); }, [load]);

  // Resolve the current user id once. /auth/me wraps as { data: { user } } and
  // api() unwraps one level (same pattern as ProfileScreen). Best-effort — a
  // failure just leaves myId null (Leave stays enabled, server still rejects).
  useEffect(() => {
    let active = true;
    api<{ user?: any }>('/auth/me').then((res) => {
      if (!active) return;
      const me = res.success ? (res.data?.user ?? res.data) : null;
      if (me?.id) setMyId(String(me.id));
    });
    return () => { active = false; };
  }, []);

  // Load notification prefs once (member view only shows them, but loading is
  // cheap and never throws).
  useEffect(() => {
    const sid = String(id || '');
    if (!sid) return;
    let active = true;
    loadCommunityPrefs(sid).then((p) => { if (active) { setPrefs(p); setPrefsReady(true); } });
    return () => { active = false; };
  }, [id]);

  // LR5: live member count. Join the server's realtime room and update the count
  // in place when ANY user joins/leaves (server emits `server:member_update` with
  // the authoritative count). Reconnect-safe — joinServerRoom auto-rejoins. No-op
  // until we have a server id; cleans up on unmount / id change.
  useEffect(() => {
    const sid = String(id || '');
    if (!sid) return;
    joinServerRoom(sid);
    const unsub = onUserEvent('server:member_update', (payload: any) => {
      if (!payload || String(payload.serverId) !== sid) return;
      const count = Number(payload.memberCount);
      if (!Number.isFinite(count)) return;
      setState((prev) => (prev.kind === 'member'
        ? { kind: 'member', server: { ...prev.server, memberCount: count } }
        : prev));
    });
    return () => {
      unsub();
      leaveServerRoom(sid);
    };
  }, [id]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  }, [load]);

  async function handleJoin() {
    const sid = String(id || '');
    if (!sid || joining) return;
    setJoining(true);
    const res = await api(`/servers/${encodeURIComponent(sid)}/join`, { method: 'POST' });
    setJoining(false);
    if (res.success) {
      setState({ kind: 'loading' });
      await load();
    } else {
      Alert.alert('Could not join', res.error || 'Please try again.');
    }
  }

  function handleLeave(server: ServerData) {
    // Owners cannot leave — the server rejects it. Say so up front instead of
    // showing a Leave confirm the backend will bounce. Ownership is derived the
    // same way as the member "Owner" badge (ownerId vs the current user id).
    const owns = !!myId && !!server.ownerId && server.ownerId === myId;
    if (owns) {
      Alert.alert(
        "You're the owner",
        'Owners cannot leave their own community. Transfer ownership or delete the community first.',
        [{ text: 'OK', style: 'cancel' }],
      );
      return;
    }
    Alert.alert('Leave Community', `Leave ${server.name}?`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Leave', style: 'destructive', onPress: async () => {
          const res = await api(`/servers/${encodeURIComponent(server.id)}/leave`, { method: 'POST' });
          if (res.success) { navigation.goBack(); }
          else { Alert.alert('Could not leave', res.error || 'Owners cannot leave their own community.'); }
        },
      },
    ]);
  }

  // Share via the native sheet with the app's registered deep link
  // (linking.ts: ServerDetail ↔ 'servers/:id'). iOS's sheet includes Copy,
  // which covers copy-link without a clipboard dependency.
  async function handleShare(name: string, sid: string) {
    const url = `cryb://servers/${encodeURIComponent(sid)}`;
    try {
      await Share.share({ message: `Join ${name} on CRYB — ${url}`, url });
    } catch { /* user dismissed the sheet */ }
  }

  function updatePrefs(patch: Partial<CommunityPrefs>) {
    const sid = String(id || '');
    setPrefs((prev) => {
      const next = { ...prev, ...patch };
      // Persist optimistically: local always, backend when the endpoint lands.
      saveCommunityPrefs(sid, next);
      return next;
    });
  }

  function openChannel(c: ChannelTileData) {
    if (isVoice(c)) {
      navigation.navigate('VoiceRoom', { id: c.id });
    } else {
      navigation.navigate('Channel', { id: c.id, name: c.name });
    }
  }

  // Channel search filter (across every section, on the Channels tab).
  const q = query.trim().toLowerCase();
  const filteredSections = useMemo(() => {
    if (!q) return sections;
    return sections
      .map((s) => ({ name: s.name, channels: s.channels.filter((c) => (c.name ?? '').toLowerCase().includes(q)) }))
      .filter((s) => s.channels.length > 0);
  }, [sections, q]);

  const pinned = useMemo(() => (q ? [] : pickPinned(sections)), [sections, q]);
  const pinnedIds = useMemo(() => new Set(pinned.map((p) => p.id)), [pinned]);

  const headerTitle = state.kind === 'member' ? state.server.name
    : state.kind === 'join' ? state.preview.name
    : 'Community';
  const headerIcon = state.kind === 'member' ? state.server.icon
    : state.kind === 'join' ? state.preview.icon
    : null;
  const isMember = state.kind === 'member';

  // ---- Shared header (all states) ----
  const header = (
    <CommunityHeader
      title={headerTitle}
      icon={headerIcon}
      iconGradient={gradientFor(String(id || headerTitle))}
      onBack={() => navigation.goBack()}
      onTitlePress={isMember ? () => setTab('info') : undefined}
      onSearch={isMember ? () => {
        setTab('channels');
        setSearchOpen((open) => { if (open) setQuery(''); return !open; });
      } : undefined}
      searchActive={searchOpen}
      onBell={isMember ? () => navigation.navigate('Notifications') : undefined}
      onSettings={isMember ? () => setTab('prefs') : undefined}
      onOverflow={isMember ? () => setMenuOpen(true) : undefined}
    />
  );

  // ---- Loading ----
  if (state.kind === 'loading') {
    return (
      <View style={styles.screen}>
        <ParticleBackground />
        {header}
        <View style={[styles.flex, styles.center]}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </View>
    );
  }

  // ---- Offline / error ----
  if (state.kind === 'offline') {
    return (
      <View style={styles.screen}>
        <ParticleBackground />
        {header}
        <View style={[styles.flex, styles.center]}>
          <Asset3D name="error" size={120} style={{ marginBottom: spacing.sm }} />
          <Text style={styles.errorText}>Couldn't load this community</Text>
          <Text style={styles.errorSub}>Check your connection and try again.</Text>
          <TouchableOpacity onPress={() => { setState({ kind: 'loading' }); load(); }} style={styles.backBtn}>
            <Text style={styles.backBtnText}>Retry</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  // ---- Not found ----
  if (state.kind === 'notfound') {
    return (
      <View style={styles.screen}>
        <ParticleBackground />
        {header}
        <View style={[styles.flex, styles.center]}>
          <Asset3D name="error" size={120} style={{ marginBottom: spacing.sm }} />
          <Text style={styles.errorText}>Community not found</Text>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn}>
            <Text style={styles.backBtnText}>Go back</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  // ---- Non-member → Join CTA ----
  if (state.kind === 'join') {
    const p = state.preview;
    const grad = gradientFor(p.id || p.name);
    return (
      <View style={styles.screen}>
        <ParticleBackground />
        {header}
        <ScrollView contentContainerStyle={[styles.content, containerStyle]} showsVerticalScrollIndicator={false}>
          <LinearGradient colors={grad} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={styles.hero}>
            <View style={styles.heroIcon}>
              {p.icon ? <CachedImage source={{ uri: p.icon }} style={styles.heroIconImg} />
                : <Text style={styles.heroIconLetter}>{(p.name.charAt(0) || 'C').toUpperCase()}</Text>}
            </View>
            <Text style={styles.heroName} numberOfLines={1}>{p.name}</Text>
            {!!p.description && <Text style={styles.heroDesc} numberOfLines={3}>{p.description}</Text>}
            <View style={styles.heroMeta}>
              <Ionicons name="people" size={14} color="rgba(255,255,255,0.92)" />
              <Text style={styles.heroMetaText}>{pluralize(p.memberCount ?? 0, 'member')}</Text>
            </View>
          </LinearGradient>

          <GlassCard style={styles.joinPanel} padding={spacing.xl} contentStyle={styles.joinPanelContent}>
            <View style={styles.joinLockCircle}>
              <Ionicons name="lock-closed-outline" size={26} color={colors.primary} />
            </View>
            <Text style={styles.joinPanelTitle}>Join to view channels</Text>
            <Text style={styles.joinPanelSub}>You're not a member yet. Join to see channels, members and chat.</Text>
            <GlassButton
              title="Join Community"
              icon="add-circle-outline"
              onPress={handleJoin}
              loading={joining}
              disabled={joining}
              fullWidth
              size="lg"
              style={styles.joinCta}
            />
          </GlassCard>
        </ScrollView>
      </View>
    );
  }

  // ---- Member view ----
  const server = state.server;
  const channelCount = server.channels.filter((c) => !isCategory(c)).length;

  // Overflow: ONLY real, wired actions (no Report — no server-report endpoint;
  // no Copy-link — no clipboard dep, iOS Share includes Copy; no gear/settings —
  // no server-settings surface exists).
  const overflowActions: OverflowAction[] = [
    {
      key: 'share', label: 'Share community', icon: 'share-outline',
      onPress: () => handleShare(server.name, server.id),
    },
    {
      key: 'leave', label: 'Leave community', icon: 'exit-outline', destructive: true,
      onPress: () => handleLeave(server),
    },
  ];

  const MemberRow = ({ m }: { m: Member }) => (
    <GlassCard
      style={styles.memberRow}
      padding={spacing.md}
      contentStyle={styles.memberRowInner}
      onPress={() => { if (m.username) navigation.navigate('UserProfile', { username: m.username }); }}
      accessibilityRole="button"
      accessibilityLabel={`View ${m.displayName || m.username || 'member'}`}
    >
      {m.avatar ? (
        <CachedImage source={{ uri: m.avatar }} style={styles.memberAvatar} />
      ) : (
        <View style={[styles.memberAvatar, styles.memberAvatarFallback]}>
          <Text style={styles.memberAvatarLetter}>{(m.displayName.charAt(0) || m.username.charAt(0) || 'U').toUpperCase()}</Text>
        </View>
      )}
      <View style={{ flex: 1 }}>
        <Text style={styles.memberName} numberOfLines={1}>{m.displayName || m.username || 'Member'}</Text>
        {!!m.username && <Text style={styles.memberHandle} numberOfLines={1}>@{m.username}</Text>}
      </View>
      {m.isOwner && (
        <View style={styles.ownerBadge}>
          <Ionicons name="star" size={11} color={colors.white} />
          <Text style={styles.ownerBadgeText}>Owner</Text>
        </View>
      )}
    </GlassCard>
  );

  const StatRow = ({ icon, label, value }: { icon: any; label: string; value: string }) => (
    <View style={styles.statRow}>
      <View style={styles.statIcon}><Ionicons name={icon} size={15} color={colors.primary} /></View>
      <Text style={styles.statLabel}>{label}</Text>
      <Text style={styles.statValue}>{value}</Text>
    </View>
  );

  const createdLabel = (() => {
    if (!server.createdAt) return '';
    const d = new Date(server.createdAt);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  })();

  const NOTIF_OPTIONS: { key: NotifLevel; label: string; sub: string; icon: any }[] = [
    { key: 'all', label: 'All messages', sub: 'Every new message in this community', icon: 'notifications' },
    { key: 'mentions', label: 'Mentions only', sub: 'Only when someone mentions you', icon: 'at' },
    { key: 'none', label: 'Nothing', sub: 'No notifications from this community', icon: 'notifications-off' },
  ];

  return (
    <View style={styles.screen}>
      <ParticleBackground />
      {header}

      <SegmentedTabs<TabKey>
        tabs={[
          { key: 'channels', label: 'Channels' },
          { key: 'info', label: 'Key Info' },
          { key: 'prefs', label: 'Preferences' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {/* In-screen channel search (header search icon toggles it). */}
      {searchOpen && tab === 'channels' && (
        <GlassSurface variant="bar" radius={radius.full} style={styles.searchWrap} contentStyle={styles.searchContent}>
          <Ionicons name="search" size={16} color={colors.muted} />
          <TextInput
            style={styles.searchInput}
            placeholder="Search channels…"
            placeholderTextColor={colors.muted}
            value={query}
            onChangeText={setQuery}
            autoFocus
            autoCapitalize="none"
            autoCorrect={false}
          />
          {query.length > 0 && (
            <TouchableOpacity onPress={() => setQuery('')} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }} accessibilityRole="button" accessibilityLabel="Clear search">
              <Ionicons name="close-circle" size={16} color={colors.muted} />
            </TouchableOpacity>
          )}
        </GlassSurface>
      )}

      <ScrollView
        contentContainerStyle={[styles.content, containerStyle]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} />}
      >
        {tab === 'channels' && (
          <>
            {/* Pinned/featured tiles (hidden while searching). */}
            {pinned.length > 0 && (
              <View style={styles.pinnedRow}>
                {pinned.map((c) => (
                  <PinnedChannelTile key={c.id} channel={c} onPress={() => openChannel(c)} />
                ))}
              </View>
            )}

            {/* Grouped channel grids. */}
            {filteredSections.map((section) => {
              const gridChannels = q
                ? section.channels
                : section.channels.filter((c) => !pinnedIds.has(c.id));
              if (gridChannels.length === 0) return null;
              return (
                <View key={section.name} style={styles.section}>
                  <Text style={styles.sectionTitle}>{section.name}</Text>
                  <View style={styles.grid}>
                    {gridChannels.map((c) => (
                      <ChannelGridTile key={c.id} channel={c} onPress={() => openChannel(c)} />
                    ))}
                  </View>
                </View>
              );
            })}

            {q.length > 0 && filteredSections.length === 0 && (
              <View style={styles.empty}>
                <Asset3D name="compass" size={100} style={{ marginBottom: spacing.sm }} />
                <Text style={styles.emptyText}>No channels match "{query.trim()}"</Text>
              </View>
            )}

            {q.length === 0 && sections.length === 0 && (
              <View style={styles.empty}>
                <Asset3D name="chat" size={100} style={{ marginBottom: spacing.sm }} />
                <Text style={styles.emptyText}>No channels yet</Text>
                <Text style={styles.emptySub}>Channels will appear here once they're created.</Text>
              </View>
            )}
          </>
        )}

        {tab === 'info' && (
          <>
            {/* About */}
            <GlassCard style={styles.infoCard} padding={spacing.lg}>
              <Text style={styles.infoTitle}>About</Text>
              <Text style={styles.infoBody}>{server.description || 'No description yet.'}</Text>
            </GlassCard>

            {/* Stats */}
            <GlassCard style={styles.infoCard} padding={spacing.lg}>
              <Text style={styles.infoTitle}>Stats</Text>
              <StatRow icon="people" label="Members" value={(server.memberCount ?? 0).toLocaleString()} />
              <StatRow icon="chatbox-ellipses" label="Channels" value={channelCount.toLocaleString()} />
              {!!createdLabel && <StatRow icon="calendar" label="Created" value={createdLabel} />}
            </GlassCard>

            {/* Members */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>{pluralize(server.members.length, 'member')}</Text>
              {server.members.map((m) => <MemberRow key={m.id} m={m} />)}
              {server.members.length === 0 && (
                <View style={styles.empty}>
                  <Asset3D name="community" size={100} style={{ marginBottom: spacing.sm }} />
                  <Text style={styles.emptyText}>No members to show</Text>
                </View>
              )}
            </View>
          </>
        )}

        {tab === 'prefs' && (
          <>
            <GlassCard style={styles.infoCard} padding={spacing.lg}>
              <Text style={styles.infoTitle}>Notifications</Text>
              {!prefsReady ? (
                <ActivityIndicator color={colors.primary} style={{ paddingVertical: spacing.lg }} />
              ) : (
                NOTIF_OPTIONS.map((opt) => {
                  const active = !prefs.muted && prefs.level === opt.key;
                  return (
                    <TouchableOpacity
                      key={opt.key}
                      style={styles.prefRow}
                      activeOpacity={0.7}
                      onPress={() => updatePrefs({ level: opt.key, muted: false })}
                      accessibilityRole="radio"
                      accessibilityState={{ selected: active }}
                      accessibilityLabel={opt.label}
                    >
                      <View style={[styles.statIcon, active && { backgroundColor: colors.primary }]}>
                        <Ionicons name={opt.icon} size={15} color={active ? '#FFFFFF' : colors.primary} />
                      </View>
                      <View style={{ flex: 1 }}>
                        <Text style={styles.prefLabel}>{opt.label}</Text>
                        <Text style={styles.prefSub}>{opt.sub}</Text>
                      </View>
                      <Ionicons
                        name={active ? 'radio-button-on' : 'radio-button-off'}
                        size={20}
                        color={active ? colors.primary : colors.faint}
                      />
                    </TouchableOpacity>
                  );
                })
              )}
            </GlassCard>

            <GlassCard style={styles.infoCard} padding={spacing.lg}>
              <View style={styles.muteRow}>
                <View style={[styles.statIcon, prefs.muted && { backgroundColor: colors.error + '18' }]}>
                  <Ionicons name="moon" size={15} color={prefs.muted ? colors.error : colors.primary} />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.prefLabel}>Mute community</Text>
                  <Text style={styles.prefSub}>Silence everything until you unmute</Text>
                </View>
                <Switch
                  value={prefs.muted}
                  onValueChange={(v) => updatePrefs({ muted: v })}
                  trackColor={{ false: colors.faint, true: colors.primary }}
                  thumbColor="#FFFFFF"
                  accessibilityLabel="Mute community"
                />
              </View>
            </GlassCard>

            <Text style={styles.prefsFootnote}>
              Preferences are saved to this device and sync with your account when available.
            </Text>
          </>
        )}
      </ScrollView>

      <OverflowMenu
        visible={menuOpen}
        onClose={() => setMenuOpen(false)}
        title={server.name}
        actions={overflowActions}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  // Transparent so the full-bleed aurora ParticleBackground shows through the
  // body; CommunityHeader owns the safe area + its own translucent surface.
  screen: { flex: 1, backgroundColor: 'transparent' },
  flex: { flex: 1 },
  center: { justifyContent: 'center', alignItems: 'center', gap: spacing.sm, padding: spacing.xl },
  content: { padding: layoutTokens.gutter, paddingBottom: layoutTokens.bottomNavInset },
  errorText: { ...typographyScale.h3, color: colors.ink, textAlign: 'center' },
  errorSub: { ...typographyScale.body, color: colors.muted, textAlign: 'center' },
  backBtn: { backgroundColor: colors.primary, borderRadius: radius.full, paddingHorizontal: spacing.xl, paddingVertical: spacing.sm, marginTop: spacing.sm },
  backBtnText: { ...fonts.semibold, color: colors.white, ...typographyScale.body },
  // Join preview hero — a saturated gradient block; white text is intentional
  // (on-gradient), mirroring the design system's on-dark treatment.
  hero: { borderRadius: glassTokens.radius.xl, padding: spacing.xl, alignItems: 'center', marginBottom: spacing.md },
  heroIcon: { width: 64, height: 64, borderRadius: 20, backgroundColor: 'rgba(255,255,255,0.22)', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', marginBottom: spacing.md },
  heroIconImg: { width: 64, height: 64, borderRadius: 20 },
  heroIconLetter: { ...typographyScale.h2, color: colors.white },
  heroName: { ...typographyScale.h2, color: colors.white, textAlign: 'center' },
  heroDesc: { ...typographyScale.caption, color: 'rgba(255,255,255,0.9)', textAlign: 'center', marginTop: spacing.xs, lineHeight: 18 },
  heroMeta: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginTop: spacing.md },
  heroMetaText: { ...fonts.semibold, ...typographyScale.caption, color: 'rgba(255,255,255,0.92)' },
  // Join panel — real glass card (surface/border/shadow come from GlassCard).
  joinPanel: { marginTop: spacing.sm },
  joinPanelContent: { alignItems: 'center', gap: spacing.sm },
  joinLockCircle: {
    width: 60, height: 60, borderRadius: radius.full,
    backgroundColor: colors.primary + '18', alignItems: 'center', justifyContent: 'center',
    marginBottom: spacing.xs,
  },
  joinPanelTitle: { ...typographyScale.h3, color: colors.ink, marginTop: spacing.xs },
  joinPanelSub: { ...typographyScale.caption, color: colors.muted, textAlign: 'center', lineHeight: 18, marginBottom: spacing.sm },
  joinCta: { width: '100%', marginTop: spacing.xs },
  // Search — glass bar; outer style positions, inner lays out the row.
  searchWrap: { marginHorizontal: layoutTokens.gutter, marginTop: spacing.md },
  searchContent: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
  },
  searchInput: { flex: 1, ...typographyScale.body, color: colors.ink, padding: 0 },
  // Channels tab
  pinnedRow: { flexDirection: 'row', gap: spacing.md, marginBottom: spacing.xl },
  section: { marginBottom: spacing.xl },
  sectionTitle: { ...typographyScale.h3, color: colors.ink, marginBottom: spacing.md, marginLeft: 2 },
  grid: { flexDirection: 'row', flexWrap: 'wrap', rowGap: spacing.md, columnGap: spacing.md, justifyContent: 'space-between' },
  empty: { alignItems: 'center', paddingVertical: 40, gap: spacing.sm },
  emptyText: { ...typographyScale.body, ...fonts.semibold, color: colors.muted, textAlign: 'center' },
  emptySub: { ...typographyScale.caption, color: colors.muted, textAlign: 'center', paddingHorizontal: spacing.xl },
  // Key Info tab
  infoCard: { marginBottom: spacing.md },
  infoTitle: { ...typographyScale.h3, color: colors.ink, marginBottom: spacing.sm },
  infoBody: { ...typographyScale.body, color: colors.ink2 },
  statRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, paddingVertical: spacing.sm },
  statIcon: { width: 28, height: 28, borderRadius: 9, backgroundColor: colors.primary + '15', alignItems: 'center', justifyContent: 'center' },
  statLabel: { ...typographyScale.body, ...fonts.medium, color: colors.ink2, flex: 1 },
  statValue: { ...typographyScale.body, ...fonts.semibold, color: colors.ink },
  memberRow: { marginBottom: spacing.sm },
  memberRowInner: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  memberAvatar: { width: 38, height: 38, borderRadius: 19, backgroundColor: colors.faint },
  memberAvatarFallback: { backgroundColor: colors.primary + '20', alignItems: 'center', justifyContent: 'center' },
  memberAvatarLetter: { ...fonts.bold, fontSize: 15, color: colors.primary },
  memberName: { ...typographyScale.body, ...fonts.semibold, color: colors.ink },
  memberHandle: { ...typographyScale.caption, color: colors.muted, marginTop: 1 },
  ownerBadge: { flexDirection: 'row', alignItems: 'center', gap: 3, backgroundColor: colors.primary, borderRadius: radius.full, paddingHorizontal: spacing.sm, paddingVertical: 3 },
  ownerBadgeText: { ...fonts.bold, fontSize: 10, color: colors.white },
  // Preferences tab
  prefRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, paddingVertical: spacing.md },
  prefLabel: { ...typographyScale.body, ...fonts.semibold, color: colors.ink },
  prefSub: { ...typographyScale.caption, color: colors.muted, marginTop: 1 },
  muteRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  prefsFootnote: { ...typographyScale.caption, color: colors.muted, textAlign: 'center', paddingHorizontal: spacing.xl, marginTop: spacing.xs },
});
